import React, { useMemo } from 'react';

import classNames from 'classnames';

import { useCommunityViewContext } from 'components/View/CommunityViewContext';
import { AGGREGATION_TYPE, AGGREGATION_TYPE_LABELS } from 'components/View/constants';
import { useAppSelector } from 'store/hook';

import RoomTypeDropdown from './RoomTypeDropdown';
import { RoomTypeGroupOption } from './types';
import { CMA_SUMMARY_INITIAL_FILTERS, CMA_SUMMARY_ROOM_TYPE_GROUPING } from '../constants';
import { CMASummaryFilters } from '../types';

const CMASummaryRoomTypeFilter = () => {
  const { apartmentTypes, viewFilters, setViewFilters } = useCommunityViewContext();
  const { apartmentTypeGroups } = useAppSelector((state) => state.global);
  const filters = viewFilters as CMASummaryFilters | undefined;

  const selectedValues = filters?.roomTypes ?? [];
  const groupingValue = filters?.roomTypeGrouping ?? CMA_SUMMARY_ROOM_TYPE_GROUPING.GROUP;
  const roomTypeFilterComparisonType =
    filters?.roomTypeFilterComparisonType ?? CMA_SUMMARY_INITIAL_FILTERS.roomTypeFilterComparisonType;
  const comparisonSqftRange = filters?.roomComparisonSqftRange ?? CMA_SUMMARY_INITIAL_FILTERS.roomComparisonSqftRange;
  const showClosestBeyondRange =
    filters?.roomComparisonShowClosestBeyondRange ?? CMA_SUMMARY_INITIAL_FILTERS.roomComparisonShowClosestBeyondRange;
  const roomTypesPriceDisplay = filters?.roomTypesPriceDisplay ?? CMA_SUMMARY_INITIAL_FILTERS.roomTypesPriceDisplay;
  const aggregationLabel =
    AGGREGATION_TYPE_LABELS[roomTypesPriceDisplay] ?? AGGREGATION_TYPE_LABELS[AGGREGATION_TYPE.AVG];

  const options = useMemo(
    () =>
      (apartmentTypes ?? []).map((item: any) => ({
        label: item.type,
        value: item.id,
      })),
    [apartmentTypes]
  );

  const groupedOptions = useMemo<RoomTypeGroupOption[]>(() => {
    if (!apartmentTypeGroups?.length) {
      return [];
    }

    const availableIds = new Set((apartmentTypes ?? []).map((item: any) => item.id));
    const hasAvailableIds = availableIds.size > 0;

    return apartmentTypeGroups
      .map((group) => {
        const variants =
          group?.apartmentTypes?.filter((variant: any) => !hasAvailableIds || availableIds.has(variant.id)) ?? [];

        if (!variants.length) {
          return null;
        }

        return {
          id: group.id,
          label: group.name,
          apartmentTypes: variants,
        };
      })
      .filter((group): group is RoomTypeGroupOption => Boolean(group));
  }, [apartmentTypeGroups, apartmentTypes]);

  const isFilterActive =
    (selectedValues && selectedValues.length > 0) ||
    roomTypeFilterComparisonType !== CMA_SUMMARY_INITIAL_FILTERS.roomTypeFilterComparisonType ||
    comparisonSqftRange !== CMA_SUMMARY_INITIAL_FILTERS.roomComparisonSqftRange ||
    showClosestBeyondRange !== CMA_SUMMARY_INITIAL_FILTERS.roomComparisonShowClosestBeyondRange;

  const handleApply = ({
    selectedValues: newSelectedValues,
    groupingValue: newGroupingValue,
    roomTypeFilterComparisonType: newComparisonType,
    comparisonSqftRange: newComparisonSqftRange,
    showClosestBeyondRange: newShowClosestBeyondRange,
    roomTypesPriceDisplay: newRoomTypesPriceDisplay,
  }: {
    selectedValues: number[];
    groupingValue: number;
    roomTypeFilterComparisonType: number;
    comparisonSqftRange: number;
    showClosestBeyondRange: boolean;
    roomTypesPriceDisplay: number;
  }) => {
    setViewFilters((prevState: any) => ({
      ...prevState,
      roomTypes: newSelectedValues,
      roomTypeGrouping: newGroupingValue,
      roomTypeFilterComparisonType: newComparisonType,
      roomComparisonSqftRange: newComparisonSqftRange,
      roomComparisonShowClosestBeyondRange: newShowClosestBeyondRange,
      roomTypesPriceDisplay: newRoomTypesPriceDisplay,
    }));
  };

  return (
    <RoomTypeDropdown
      className={classNames({ active: isFilterActive })}
      placeholder={'Room Type'}
      options={options}
      selectedValues={selectedValues}
      groupingValue={groupingValue}
      roomTypeComparisonType={roomTypeFilterComparisonType}
      comparisonSqftRange={comparisonSqftRange}
      showClosestBeyondRange={showClosestBeyondRange}
      roomTypesPriceDisplay={roomTypesPriceDisplay}
      otherOptionsLabel={aggregationLabel}
      groupedOptions={groupedOptions}
      onApply={handleApply}
      dataTestId="room-type-filter"
    />
  );
};

export default CMASummaryRoomTypeFilter;
